/**
 * Document Verification Service
 *
 * Handles document upload and verification via verify-engine API with comprehensive
 * error handling, retry logic, and progress tracking. Supports both standalone
 * document verification and face-matching verification modes.
 *
 * Key features:
 * - Base64 to File/Blob conversion for API compatibility
 * - Exponential backoff retry logic for network resilience
 * - Progress callback system for UI feedback
 * - Comprehensive error handling and logging
 * - Support for both basic and enhanced verification modes
 * - Client-side document quality pre-analysis
 *
 * @author SafePassage Engineering
 * @version 1.0.0
 */

import { verificationLogger } from '../utils/logger';
import { getViewportDetector } from '../utils/viewportDetection';
import { runtimeConfig } from './RuntimeConfigService';
import { hasLaunchCredential } from '../utils/launchCredentials';
import {
  operationFenceFromProjection,
  type LifecycleProjection,
  type LifecycleOperationFence,
  verificationLifecycleService,
} from './VerificationLifecycleService';
import type { DocumentCaptureDiagnostics } from '../types/verification';
import { sanitizeVerificationResponseForClient } from '../utils/verificationResultSanitizer';
import { attachVerificationOperationContext } from '../utils/verificationOperationContext';

/**
 * Upper bound on one Engine document request, including its response body.
 * Engine's own work and settlement finish well inside it, but the ingress
 * keeps idle requests open for an hour, so a response lost on a suspended or
 * switched connection would otherwise hold the processing screen open. When
 * the bound ends the request, Portal decides what happens next, exactly as for
 * any other lost Engine response: the browser never resubmits or reports a
 * failure for it. It ends before Portal's 120-second processing deadline, so
 * the screen can still observe Engine's late settlement.
 */
export const ENGINE_DOCUMENT_REQUEST_TIMEOUT_MS = 110_000;

type ClientType = 'mobile' | 'desktop' | 'unknown';
type FlowPath = 'desktop_direct' | 'mobile_direct' | 'mobile_handoff' | 'unknown';
export type DocumentVerificationRetryTarget = 'document_capture' | string;
export type DocumentVerificationFailureCategory =
  | 'document_capture_quality'
  | 'identity_mismatch'
  | 'technical_face_match_failure'
  | string;

interface ClientDiagnostics {
  clientType: ClientType;
  flowPath: FlowPath;
  clientClassificationSource: string;
}

/**
 * Request structure for document verification
 *
 * @interface DocumentVerificationRequest
 * @property {string} document - Base64 encoded document image
 * @property {string} [selfie] - Base64 encoded selfie for face matching
 * @property {string} sessionId - Session ID from WebSocket verification
 * @property {string} [emblemState] - Analytics tracking identifier
 */
export interface DocumentVerificationRequest {
  document?: string; // Base64 encoded image; omitted for a face-only comparison
  selfie?: string; // Base64 encoded selfie for face matching
  /**
   * Compare a new selfie with the ID Verify Engine accepted earlier in this
   * attempt, without sending or re-checking the document.
   */
  faceMatchOnly?: boolean;
  sessionId: string; // Session ID from WebSocket verification
  sessionToken?: string; // Session token for Portal-backed document verification
  faceMatchEnabled?: boolean; // Whether selfie-vs-ID face matching is enabled
  emblemState?: string; // Analytics tracking
  documentCaptureDiagnostics?: DocumentCaptureDiagnostics;
}

/**
 * Response structure from document verification API
 *
 * @interface DocumentVerificationResponse
 * @property {boolean} success - Whether verification was successful
 * @property {Object} [data] - Verification result data
 * @property {string[]} [fraud_indicators] - Detected fraud indicators
 * @property {boolean} [is_expired] - Whether document is expired
 * @property {string} [session_id] - Session identifier
 * @property {string} [detail] - Error details for authentication issues
 * @property {string} [error] - General error message
 * @property {boolean} [age_verified] - Age verification result
 * @property {boolean} [retryable] - Whether the failed response can be retried
 * @property {string} [retryTarget] - Capture step that should be retried
 * @property {string} [failureCategory] - Machine-readable failure category
 */
export interface DocumentVerificationResponse {
  success: boolean;
  data?: {
    verification_result: 'verified' | 'failed' | 'error';
    ocr_data?: {
      type: string;
      dob: string;
      expiry?: string;
    };
    age_verified: boolean;
    session_id: string;
  };
  verification_result?: 'verified' | 'failed' | 'error';
  ocr_data?: {
    type: string;
    dob: string;
    expiry?: string;
  };
  fraud_indicators?: string[];
  is_expired?: boolean;
  session_id?: string;
  detail?: string; // For authentication errors
  error?: string;
  age_verified?: boolean; // For backward compatibility or alternative response format
  retryable?: boolean;
  retryTarget?: DocumentVerificationRetryTarget;
  failureCategory?: DocumentVerificationFailureCategory;
  status?: 'verified' | 'failed' | 'cancelled' | 'error';
  outcome?: 'verified' | 'failed' | 'cancelled' | 'error';
}

/**
 * Processing step for progress tracking
 *
 * @interface DocumentProcessingStep
 * @property {string} id - Unique step identifier
 * @property {string} label - Human-readable step description
 * @property {'pending' | 'active' | 'completed' | 'failed'} status - Current step status
 */
export interface DocumentProcessingStep {
  id: string;
  label: string;
  status: 'pending' | 'active' | 'completed' | 'failed';
}

export class DocumentVerificationStateChangedError extends Error {
  constructor(readonly lifecycle: LifecycleProjection) {
    super('Document verification state changed');
    this.name = 'DocumentVerificationStateChangedError';
  }
}

export class DocumentVerificationAuthorizationError extends Error {
  constructor(readonly status: 401 | 403) {
    super('Document verification authorization failed');
    this.name = 'DocumentVerificationAuthorizationError';
  }
}

export class DocumentVerificationUnavailableError extends Error {
  constructor() {
    super('Document verification is unavailable');
    this.name = 'DocumentVerificationUnavailableError';
  }
}

/**
 * Document Verification Service Class
 *
 * Main service class for handling document verification requests to the verify-engine.
 * Provides retry logic, progress tracking, and comprehensive error handling.
 *
 * The service converts Base64 images to File objects for proper multipart/form-data
 * submission, implements exponential backoff for network resilience, and provides
 * detailed progress callbacks for UI feedback.
 */
export class DocumentVerificationService {
  private baseUrl: string | null = null;
  private apiKey: string | null = null;
  /** Session operations whose Engine 401 was already reconciled with Portal. */
  private readonly engineRejectionsReconciled = new Set<string>();

  constructor() {
    // Base URL will be loaded from runtime config when needed
  }

  /**
   * Get base URL from runtime config
   *
   * Loads the verify-engine URL from hostname-derived runtime configuration.
   * Browser globals and persisted session state are never endpoint authorities.
   *
   * @returns {Promise<string>} The verify-engine base URL
   * @private
   */
  private async getBaseUrl(): Promise<string> {
    if (this.baseUrl) {
      return this.baseUrl;
    }

    const config = await runtimeConfig.loadConfig();
    this.baseUrl = config.engineUrl;
    return this.baseUrl;
  }

  /**
   * Set API key for authentication
   *
   * @param {string} apiKey - API key for verify-engine authentication
   * @public
   */
  public setApiKey(apiKey: string): void {
    this.apiKey = apiKey;
  }

  /**
   * Convert base64 to Blob object (following documentation best practices)
   *
   * Handles both data URL format and raw base64 strings, converting to
   * JPEG blob for API submission.
   *
   * @param {string} base64Data - Base64 encoded image data
   * @returns {Blob} JPEG blob object
   * @private
   */
  private base64ToBlob(base64Data: string): Blob {
    // Remove data URL prefix if present
    const cleanBase64 = base64Data.replace(/^data:image\/\w+;base64,/, '');

    // Convert base64 to binary
    const byteCharacters = atob(cleanBase64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: 'image/jpeg' });
  }

  /**
   * Convert base64 to File object
   *
   * Creates a File object from base64 data for multipart form submission.
   *
   * @param {string} base64 - Base64 encoded image data
   * @param {string} filename - Name for the file object
   * @returns {File} File object for form submission
   * @private
   */
  private base64ToFile(base64: string, filename: string): File {
    const blob = this.base64ToBlob(base64);
    return new File([blob], filename, { type: 'image/jpeg' });
  }

  private resolveClientDiagnostics(): ClientDiagnostics {
    const hasHandoffToken = hasLaunchCredential('handoffToken');

    if (hasHandoffToken) {
      return {
        clientType: 'mobile',
        flowPath: 'mobile_handoff',
        clientClassificationSource: 'handoff_token',
      };
    }

    try {
      const viewport = getViewportDetector().getViewport();
      if (viewport.isDesktop || viewport.isTablet) {
        return {
          clientType: 'desktop',
          flowPath: 'desktop_direct',
          clientClassificationSource: 'viewport_detector',
        };
      }

      if (viewport.isMobile) {
        return {
          clientType: 'mobile',
          flowPath: 'mobile_direct',
          clientClassificationSource: 'viewport_detector',
        };
      }
    } catch {
      // Fall through to browser capability heuristics if viewport detection is unavailable.
    }

    const navigatorWithUAData = navigator as Navigator & {
      userAgentData?: { mobile?: boolean };
    };

    if (typeof navigatorWithUAData.userAgentData?.mobile === 'boolean') {
      return {
        clientType: navigatorWithUAData.userAgentData.mobile ? 'mobile' : 'desktop',
        flowPath: navigatorWithUAData.userAgentData.mobile
          ? 'mobile_direct'
          : 'desktop_direct',
        clientClassificationSource: 'user_agent_data_mobile',
      };
    }

    if (typeof navigator.userAgent === 'string' && navigator.userAgent.length > 0) {
      const isTabletUserAgent = /iPad|Tablet/i.test(navigator.userAgent);
      const isMobileUserAgent =
        !isTabletUserAgent &&
        /Android.+Mobile|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(
          navigator.userAgent
        );

      return {
        clientType: isMobileUserAgent ? 'mobile' : 'desktop',
        flowPath: isMobileUserAgent ? 'mobile_direct' : 'desktop_direct',
        clientClassificationSource: 'user_agent_regex',
      };
    }

    return {
      clientType: 'unknown',
      flowPath: 'unknown',
      clientClassificationSource: 'unknown',
    };
  }

  /**
   * Verify document with retry logic and exponential backoff
   *
   * Implements intelligent retry strategy with exponential backoff.
   * Does not retry authentication errors (401, 403, 503) as they
   * are unlikely to succeed on retry.
   *
   * @param {DocumentVerificationRequest} request - Verification request data
   * @param {Function} [onProgress] - Progress callback function
   * @param {number} [maxRetries=3] - Maximum number of retry attempts
   * @returns {Promise<DocumentVerificationResponse>} Verification response
   * @private
   */
  private async verifyDocumentWithRetry(
    request: DocumentVerificationRequest,
    onProgress?: (step: DocumentProcessingStep) => void,
    maxRetries: number = 3
  ): Promise<DocumentVerificationResponse> {
    let attempt = 0;

    while (attempt < maxRetries) {
      try {
        return await this.performDocumentVerification(request, onProgress);
      } catch (error) {
        attempt++;
        verificationLogger.warn(
          `Document verification attempt ${attempt} failed:`,
          error instanceof Error ? error : new Error(`${error}`)
        );

        // Don't retry on authentication errors (they won't succeed on retry)
        if (
          error instanceof DocumentVerificationStateChangedError ||
          error instanceof DocumentVerificationAuthorizationError ||
          error instanceof DocumentVerificationUnavailableError ||
          error instanceof Error &&
          (error.message.includes('Invalid API key') ||
            error.message.includes('API access disabled') ||
            error.message.includes('Authentication service'))
        ) {
          throw error;
        }

        // If this was the last attempt, throw the error
        if (attempt === maxRetries) {
          throw error;
        }

        // Wait before retrying with exponential backoff
        const delay = 1000 * Math.pow(2, attempt - 1); // 1s, 2s, 4s...
        verificationLogger.debug(`Retrying in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    // This should never be reached, but TypeScript requires it
    throw new Error('Retry logic failed unexpectedly');
  }

  /**
   * Verify document with optional face matching
   *
   * Main public method for document verification. Delegates to retry logic
   * for network resilience.
   *
   * @param {DocumentVerificationRequest} request - Verification request
   * @param {Function} [onProgress] - Progress callback for UI updates
   * @returns {Promise<DocumentVerificationResponse>} Verification result
   * @public
   */
  public async verifyDocument(
    request: DocumentVerificationRequest,
    onProgress?: (step: DocumentProcessingStep) => void
  ): Promise<DocumentVerificationResponse> {
    return this.verifyDocumentWithRetry(request, onProgress);
  }

  /**
   * Compare a new selfie with the ID accepted earlier in this attempt. Uses
   * the same fencing, settlement reconciliation and retry handling as a full
   * document verification; only the face comparison runs.
   */
  public async verifyFaceMatch(
    request: Omit<DocumentVerificationRequest, 'document' | 'faceMatchOnly' | 'documentCaptureDiagnostics'> & {
      selfie: string;
    },
    onProgress?: (step: DocumentProcessingStep) => void
  ): Promise<DocumentVerificationResponse> {
    return this.verifyDocumentWithRetry({ ...request, faceMatchOnly: true }, onProgress);
  }

  /**
   * Perform the actual document verification API call
   *
   * Handles the complete verification flow:
   * 1. Progress tracking with UI callbacks
   * 2. Form data preparation and file conversion
   * 3. API request with proper authentication
   * 4. Response parsing and error handling
   * 5. Backend response format transformation
   *
   * @param {DocumentVerificationRequest} request - Verification request
   * @param {Function} [onProgress] - Progress callback function
   * @returns {Promise<DocumentVerificationResponse>} Verification response
   * @private
   */
  private async performDocumentVerification(
    request: DocumentVerificationRequest,
    onProgress?: (step: DocumentProcessingStep) => void
  ): Promise<DocumentVerificationResponse> {
    if (!this.apiKey) {
      throw new Error('API key not set');
    }

    let operationSource: LifecycleOperationFence | null = null;
    let portalBackedEngineRequestStarted = false;
    const engineRequest = new AbortController();
    let engineRequestTimer: ReturnType<typeof setTimeout> | undefined;

    try {
      // Update progress: Document received
      onProgress?.({
        id: 'received',
        label: 'Document received',
        status: 'completed',
      });

      // Update progress: Analyzing information
      onProgress?.({
        id: 'analyzing',
        label: 'Analyzing information',
        status: 'active',
      });

      // Prepare form data
      const formData = new FormData();
      const config =
        request.faceMatchEnabled === undefined || !request.sessionToken
          ? await runtimeConfig.loadConfig().catch(() => null)
          : null;
      const faceMatchEnabled =
        request.faceMatchEnabled ?? config?.faceMatchEnabled ?? true;
      const sessionToken = request.sessionToken ?? config?.sessionToken;
      await verificationLifecycleService.alignToScreen('id-processing');
      const lifecycle = await verificationLifecycleService.startIfNeeded();
      if (!lifecycle.attempt || lifecycle.attempt.status !== 'ACTIVE') {
        throw new DocumentVerificationUnavailableError();
      }
      operationSource = operationFenceFromProjection(lifecycle);

      // Add document file (a face-only comparison uses the retained ID)
      const documentFile = request.faceMatchOnly || !request.document
        ? null
        : this.base64ToFile(request.document, 'document.jpg');
      if (documentFile) {
        formData.append('document', documentFile);
      } else if (!request.faceMatchOnly) {
        throw new Error('A document image is required');
      }

      // Add selfie if provided
      if (request.selfie) {
        const selfieFile = this.base64ToFile(request.selfie, 'selfie.jpg');
        formData.append('selfie', selfieFile);
      }

      // Add other parameters
      formData.append('session_id', request.sessionId);
      if (!request.faceMatchOnly) {
        formData.append('face_match_enabled', String(faceMatchEnabled));
      }
      if (sessionToken) {
        formData.append('session_token', sessionToken);
      }
      formData.append('workflow_attempt_id', lifecycle.attempt.id);
      formData.append('workflow_attempt_generation', String(lifecycle.attempt.generation));
      formData.append('workflow_attempt_version', String(lifecycle.attempt.version));
      if (lifecycle.attempt.operation) {
        formData.append('operation_id', lifecycle.attempt.operation.id);
        formData.append('operation_generation', String(lifecycle.attempt.operation.generation));
      }
      if (request.emblemState) {
        formData.append('emblem_state', request.emblemState);
      }
      const clientDiagnostics = this.resolveClientDiagnostics();
      formData.append('client_type', clientDiagnostics.clientType);
      formData.append('flow_path', clientDiagnostics.flowPath);
      formData.append(
        'client_classification_source',
        clientDiagnostics.clientClassificationSource
      );
      const documentUploadBytes = documentFile?.size ?? 0;
      const documentCaptureDiagnostics = request.documentCaptureDiagnostics
        ? {
            ...request.documentCaptureDiagnostics,
            finalUploadBytes: documentUploadBytes,
          }
        : undefined;

      if (documentCaptureDiagnostics && documentFile) {
        formData.append(
          'document_capture_diagnostics',
          JSON.stringify(documentCaptureDiagnostics)
        );
      }

      // Make API request to correct endpoint
      const baseUrl = await this.getBaseUrl();
      verificationLogger.info('Submitting document verification request', {
        labels: {
          sessionId: request.sessionId,
          hasSelfie: `${!!request.selfie}`,
          clientType: clientDiagnostics.clientType,
          flowPath: clientDiagnostics.flowPath,
          clientClassificationSource: clientDiagnostics.clientClassificationSource,
          documentUploadBytes: `${documentUploadBytes}`,
          documentCaptureProfile:
            documentCaptureDiagnostics?.requestedProfileName || 'unknown',
          documentCaptureFallbackLevel:
            documentCaptureDiagnostics?.fallbackLevel === undefined
              ? 'unknown'
              : `${documentCaptureDiagnostics.fallbackLevel}`,
          documentCaptureConstraints:
            documentCaptureDiagnostics?.requestedConstraintSummary || 'unknown',
          documentNegotiatedWidth:
            documentCaptureDiagnostics?.negotiatedWidth === null ||
            documentCaptureDiagnostics?.negotiatedWidth === undefined
              ? 'unknown'
              : `${documentCaptureDiagnostics.negotiatedWidth}`,
          documentNegotiatedHeight:
            documentCaptureDiagnostics?.negotiatedHeight === null ||
            documentCaptureDiagnostics?.negotiatedHeight === undefined
              ? 'unknown'
              : `${documentCaptureDiagnostics.negotiatedHeight}`,
          documentFullFrameWidth:
            documentCaptureDiagnostics?.fullFrameWidth === null ||
            documentCaptureDiagnostics?.fullFrameWidth === undefined
              ? 'unknown'
              : `${documentCaptureDiagnostics.fullFrameWidth}`,
          documentFullFrameHeight:
            documentCaptureDiagnostics?.fullFrameHeight === null ||
            documentCaptureDiagnostics?.fullFrameHeight === undefined
              ? 'unknown'
              : `${documentCaptureDiagnostics.fullFrameHeight}`,
          documentCropRect:
            documentCaptureDiagnostics?.cropSourceWidth === null ||
            documentCaptureDiagnostics?.cropSourceWidth === undefined ||
            documentCaptureDiagnostics?.cropSourceHeight === null ||
            documentCaptureDiagnostics?.cropSourceHeight === undefined
              ? 'unknown'
              : `${documentCaptureDiagnostics.cropSourceX ?? 0},${documentCaptureDiagnostics.cropSourceY ?? 0},${documentCaptureDiagnostics.cropSourceWidth},${documentCaptureDiagnostics.cropSourceHeight}`,
          documentFinalImageWidth:
            documentCaptureDiagnostics?.finalImageWidth === null ||
            documentCaptureDiagnostics?.finalImageWidth === undefined
              ? 'unknown'
              : `${documentCaptureDiagnostics.finalImageWidth}`,
          documentFinalImageHeight:
            documentCaptureDiagnostics?.finalImageHeight === null ||
            documentCaptureDiagnostics?.finalImageHeight === undefined
              ? 'unknown'
              : `${documentCaptureDiagnostics.finalImageHeight}`,
          documentAccepted:
            documentCaptureDiagnostics?.accepted === undefined
              ? 'unknown'
              : `${documentCaptureDiagnostics.accepted}`,
          documentAcceptanceReason:
            documentCaptureDiagnostics?.acceptanceReason || 'unknown',
        },
      });
      portalBackedEngineRequestStarted = Boolean(sessionToken);
      engineRequestTimer = setTimeout(
        () => engineRequest.abort(),
        ENGINE_DOCUMENT_REQUEST_TIMEOUT_MS,
      );
      const response = await fetch(
        `${baseUrl}/api/document/${request.faceMatchOnly ? 'face-match' : 'verify'}`,
        {
          method: 'POST',
          headers: {
            'X-API-Key': this.apiKey,
          },
          body: formData,
          signal: engineRequest.signal,
        }
      );

      // Update progress: Verifying authenticity
      onProgress?.({
        id: 'analyzing',
        label: 'Analyzing information',
        status: 'completed',
      });
      onProgress?.({
        id: 'verifying',
        label: 'Verifying authenticity',
        status: 'active',
      });

      // Handle authentication and other errors according to documentation
      if (!response.ok) {
        await response.json().catch(() => ({}));

        if (response.status === 409) {
          const current = await verificationLifecycleService.reconcile();
          throw new DocumentVerificationStateChangedError(current);
        }

        // Handle specific error cases from documentation
        if (response.status === 401 || response.status === 403) {
          const operationKey = `${request.sessionId}:${operationSource?.operationId ?? 'none'}`;
          if (
            portalBackedEngineRequestStarted &&
            response.status === 401 &&
            !this.engineRejectionsReconciled.has(operationKey)
          ) {
            // Engine revisions before completion resilience also answered 401
            // when Portal token validation was unavailable (5xx/timeout). Only
            // a rejection Portal confirms for this bearer ends the flow;
            // otherwise Portal state decides, once per operation, so a real
            // Engine-side rejection cannot loop the user through capture.
            this.engineRejectionsReconciled.add(operationKey);
            const confirmation =
              await verificationLifecycleService.confirmCredentialRejection(5_000);
            if (confirmation.kind === 'accepted') {
              throw new DocumentVerificationStateChangedError(confirmation.lifecycle);
            }
            if (confirmation.kind === 'unavailable') {
              throw new DocumentVerificationUnavailableError();
            }
          }
          throw new DocumentVerificationAuthorizationError(
            response.status as 401 | 403
          );
        }
        if (response.status === 503) {
          throw new DocumentVerificationUnavailableError();
        }

        throw portalBackedEngineRequestStarted
          ? new DocumentVerificationUnavailableError()
          : new Error('verification_failed');
      }

      const result = await response.json();
      const transformedResult = sanitizeVerificationResponseForClient(result, {
        fallbackSessionId: request.sessionId,
        fallbackFailureCategory: 'document_verification_failed',
      });

      // Update progress: Confirming age
      onProgress?.({
        id: 'verifying',
        label: 'Verifying authenticity',
        status: 'completed',
      });
      onProgress?.({
        id: 'confirming',
        label: 'Confirming age',
        status: 'active',
      });

      // Final progress update
      setTimeout(() => {
        onProgress?.({
          id: 'confirming',
          label: 'Confirming age',
          status: transformedResult.success ? 'completed' : 'failed',
        });
      }, 500);

      if (typeof result !== 'object' || result === null) {
        // Unexpected format
        verificationLogger.error('Unexpected document verification response format');
        throw new Error('Unexpected API response format');
      }

      verificationLogger.debug('Document verification response received', {
        labels: {
          success: `${transformedResult.success}`,
          status: transformedResult.status,
          failureCategory: transformedResult.failureCategory || 'none',
          sessionId: transformedResult.session_id,
        }
      });

      if (!transformedResult.success) {
        verificationLogger.info('Document verification failed', {
          labels: {
            sessionId: transformedResult.session_id,
            failureCategory:
              transformedResult.failureCategory || 'document_verification_failed',
          },
        });
      } else {
        verificationLogger.info('Document verification successful', {
          labels: { sessionId: transformedResult.session_id },
        });
      }

      return attachVerificationOperationContext(transformedResult, operationSource);
    } catch (error) {
      if (
        error instanceof DocumentVerificationStateChangedError ||
        error instanceof DocumentVerificationAuthorizationError
      ) {
        throw error;
      }
      if (portalBackedEngineRequestStarted) {
        // A lost Engine/Portal acknowledgement is not a document rejection.
        // Read Portal before routing, including network loss of the Engine
        // response itself. Never resubmit processing or report failure here.
        let current;
        try {
          current = await verificationLifecycleService.reconcile(5_000);
        } catch {
          throw new DocumentVerificationUnavailableError();
        }
        throw new DocumentVerificationStateChangedError(current);
      }
      if (error instanceof DocumentVerificationUnavailableError) {
        throw new DocumentVerificationUnavailableError();
      }
      verificationLogger.error('Document verification error');

      // Update progress on error
      onProgress?.({
        id: 'analyzing',
        label: 'Analyzing information',
        status: 'failed',
      });

      return attachVerificationOperationContext({
        success: false,
        data: {
          verification_result: 'error',
          age_verified: false,
          session_id: request.sessionId,
        },
        session_id: request.sessionId,
        status: 'error',
        outcome: 'error',
        failureCategory: 'document_verification_failed',
        error: 'document_verification_failed',
      }, operationSource);
    } finally {
      clearTimeout(engineRequestTimer);
    }
  }

  /**
   * Analyze document quality (client-side pre-check)
   *
   * Performs basic client-side quality analysis to provide immediate
   * feedback before sending to server. Current implementation uses
   * heuristics; production would use computer vision libraries.
   *
   * @param {string} imageData - Base64 encoded image data
   * @returns {Object} Quality analysis results
   * @returns {boolean} returns.readable - Whether text is readable
   * @returns {boolean} returns.focused - Whether image is properly focused
   * @returns {boolean} returns.glare - Whether glare is acceptable
   * @returns {boolean} returns.corners - Whether document corners are visible
   * @returns {number} returns.overall - Overall quality score (0-1)
   * @public
   */
  public analyzeDocumentQuality(imageData: string): {
    readable: boolean;
    focused: boolean;
    glare: boolean;
    corners: boolean;
    overall: number;
  } {
    // This is a placeholder for actual image analysis
    // In production, you'd use computer vision libraries or pre-trained models
    // For now, we'll use heuristics based on image properties

    try {
      // Create an image element to analyze
      const img = new Image();
      img.src = imageData;

      // Simple heuristics (these would be replaced with real analysis)
      const quality = {
        readable: true, // Would check OCR confidence
        focused: true, // Would check blur detection
        glare: true, // Would check for bright spots
        corners: true, // Would check document boundaries
        overall: 0.85, // Overall quality score
      };

      // Random variation for demo purposes
      quality.readable = Math.random() > 0.2;
      quality.focused = Math.random() > 0.3;
      quality.glare = Math.random() > 0.4;
      quality.corners = Math.random() > 0.1;

      // Calculate overall score
      const scores = [
        quality.readable ? 1 : 0,
        quality.focused ? 1 : 0,
        quality.glare ? 1 : 0,
        quality.corners ? 1 : 0,
      ];
      quality.overall = scores.reduce((a, b) => a + b) / scores.length;

      return quality;
    } catch (error) {
      verificationLogger.error('Error analyzing document quality:', error instanceof Error ? error : new Error(`${error}`));
      return {
        readable: false,
        focused: false,
        glare: false,
        corners: false,
        overall: 0,
      };
    }
  }
}

// Singleton instance
export const documentVerificationService = new DocumentVerificationService();
